import {
  CategoryInterface,
  ItemInterface,
  ModifierListInterface,
  ModifiersInterface,
  PriceInterface,
  TagInterface,
  TaxInterface,
  validateItemInterface,
  VariationInterface,
} from "@/interface/business/item.interface";
import store from "@/store";
import { Translate as $t } from "@/plugins/translate";
import { ItemType, ModifierListType, ModifiersType, TaxType, VariationType } from "@/types/business/item.type";
import { ItemDiscountInterface } from "@/interface/business/discount.interface";
import { ItemDiscount } from "@/modules/location/index";
import { Variation, Tax, PriceMoney, ModifierList } from "@/modules/location";
import { DISCOUNT_TYPES, TAXES_INCLUSION_TYPE } from "@/enums/business.enum";
import { ItemDiscountType } from "@/types/business/discount.type";
import { NAME_LOCAL_DESCRIPTION_KEY, NAME_LOCAL_NAME_KEY } from "@/enums/orders.enum";
import { Modifier } from "@/modules/location/modifier";
import { validateOrderInterface } from "@/interface/order.inteface";
const defaultItemImage = require("@/assets/images/default-item-image.svg");

/**
 * Item Class
 * @param {ItemInterface} Item
 */
export default class Item implements ItemInterface {
  id: string = "";
  name: string = "";
  local_name?: string = "";
  color?: string = "";
  description?: string = "";
  local_description?: string = "";
  image?: string = "";
  selected?: boolean = false;
  category?: CategoryInterface;
  variations?: Array<VariationInterface>;
  modifier_lists?: Array<ModifierListInterface>;
  tags?: Array<TagInterface> = [];
  taxes: Array<TaxInterface> = [];
  quantity: number = 1;
  totalPrice: PriceInterface = new PriceMoney({ amount: 0 });
  discount?: ItemDiscountInterface;
  notes?: string;
  original_name?: string = "";
  original_local_name?: string = "";
  external_id?: string = "";
  plu?: string = "";
  image_url?: string = "";
  category_id: string = "";
  isStaged?: boolean = false;
  private modifier_list: any;
  isCustom?: boolean = false;
  sale_item_id: string = "";

  constructor(data?: ItemType) {
    if (!data) {
      return;
    }
    this.sale_item_id = data.sale_item_id || "";
    this.id = data.id || "";
    this.name = data.name || "";
    this.local_name = data.local_name || "";
    this.color = data.color || "";
    this.description = data.description || "";
    this.local_description = data.local_description || "";
    this.image = data.image || "";
    this.category = data.category;
    this.quantity = data.quantity || 1;
    this.tags = data.tags;
    this.notes = data.notes;
    this.variations = [];
    this.modifier_lists = [];
    this.original_name = data.original_name ?? data.name ?? "";
    this.original_local_name = data.original_local_name ?? data.local_name ?? "";
    this.image_url = data.image_url || "";
    this.plu = data.plu || "";
    this.external_id = data.external_id || "";
    this.category_id = data.category_id || "";
    this.isStaged = false;

    this.setupCategory(data);
    this.setupVariation(data);
    this.setupModifierList(data);
    this.setupItemDiscount(data);
    this.setTotalPrice(data);
    this.setItemTaxes(data);
    this.setIsCustom(data);
  }

  private setIsCustom(data): void {
    this.isCustom = data?.isCustom || this.variations?.[0]?.name === "custom";
  }

  private setItemTaxes(data): void {
    const taxes = data?.origin?.taxes || data?.taxes;
    this.taxes = taxes?.map((tax: TaxType): TaxInterface => new Tax(tax));
  }

  private setTotalPrice(data): void {
    const amount = data?.total || this.variations?.[0]?.price || 0;
    this.totalPrice = new PriceMoney({ amount: amount });
  }

  private setupCategory(data): any {
    this.category = data?.category || data?.origin?.category;
  }

  private setupVariation(data): any {
    let variations = data?.origin?.variations || data?.variations || data?.variation;
    this.variations = variations?.map((variation: VariationType): VariationInterface => new Variation(variation));
    // check if selected from Tab Sale
    if (data?.variation) {
      this.variations = this.variations?.map((variation) => {
        if (variation.id === data.variation.id) {
          variation.select();
        }
        return variation;
      });
    }
  }

  private setupModifierList(data): any {
    const modifier_lists = data?.origin?.modifier_lists || data.modifier_lists || data.modifier_list;

    this.modifier_lists = modifier_lists?.map(
      (list: ModifierListType): ModifierListInterface => new ModifierList(list)
    );

    // setup the selected modifiers from sale
    if (!!data?.modifiers) {
      this.modifier_lists = this.modifier_lists?.map((list) => {
        list.modifiers = list.modifiers.map((modifier) => {
          const index = data?.modifiers.findIndex((mod) => mod.id === modifier.id);
          if (index > -1) {
            modifier.select();
          } else {
            modifier.unSelect();
            modifier.quantity = 0;
          }
          return modifier;
        });
        return list;
      });
    }
  }

  private setupItemDiscount(data): any {
    this.discount = <ItemDiscountInterface>data?.discount || data?.discounts?.[0];
  }

  /**
   * init item data
   * @return {void}
   */
  public async initItem(): Promise<void> {
    await this.prepareModifier();
    await this.prepareVariations();
    await this.calculateItemTotal();
  }

  /**
   * get item name by language key
   * @return {string}
   */
  public getDisplayName(): string {
    const { state } = <any>store;
    return this?.[NAME_LOCAL_NAME_KEY[state?.App.lang]] || this.name;
  }

  /**
   * get item description by language key
   * @return {string}
   */
  public getDisplayDescription(): string {
    const { state } = <any>store;
    return this?.[NAME_LOCAL_DESCRIPTION_KEY[state?.App.lang]] || this.description;
  }

  /**
   * get item name by language key
   * @return {string}
   */
  public getDisplayImage(): string {
    console.log('this.image?.["256"] || defaultItemImage', this.image?.["256"] || defaultItemImage);
    return this.image?.["256"] || defaultItemImage;
  }

  /**
   * Reset sale item id
   * @return {void}
   */
  public resetSaleItemId(): void {
    this.sale_item_id = "";
  }

  /**
   * Prepare modifier to set default modifier selected
   * @return {void}
   */
  public prepareModifier(): void {
    this.modifier_lists?.map(<ModifiersType>(modifier): void => {
      return modifier.setModifiers();
    });
  }

  /**
   * Prepare item total price
   * @return {void}
   */
  public async calculateItemTotal(): Promise<void> {
    // this.totalPrice.setAmount(0);
    //
    // await this.calculateItemVariation();
    // await this.calculateItemModifiers();
    //
    // // calculate price for items quantity
    // let { amount } = this.totalPrice;
    // amount *= this.quantity;
    // this.totalPrice.setAmount(amount);
    //
    // this.calculateItemDiscount();
    // this.calculateItemTax();
  }

  /**
   * Prepare variations to set default variation selected
   * @return {void}
   */
  public prepareVariations(): void {
    const variation = this.variations?.[0];
    variation?.select();
    const amount = variation?.price_money?.amount || 0;
    const currency = variation?.price_money?.currency || "";
    this.totalPrice = new PriceMoney({ currency, amount });
  }

  /**
   * Prepare modifier to set default modifier selected
   * @return {void}
   */
  public setDiscount(discount: ItemDiscountType): void {
    this.discount = new ItemDiscount(discount);
  }

  /**
   * Calculate variations
   * @return {Promise<any>}
   */
  public async calculateItemVariation(): Promise<any> {
    await this.variations?.filter((variation: VariationInterface) => {
      if (variation.selected) {
        let { amount } = this.totalPrice;
        amount += variation.priceValue();
        this.totalPrice.setAmount(amount);
      }
    });
  }

  /**
   * Calculate modifiers
   * @return {Promise<void>}
   */
  public async calculateItemModifiers(): Promise<any> {
    return this.modifier_lists?.map(
      async (modifierList: ModifierListInterface): Promise<any> => {
        await modifierList.modifiers.map((modifier: ModifiersInterface) => {
          if (modifier.selected && modifier.quantity > 0) {
            let price = modifier?.price_money?.amount || 0;
            let { amount } = this.totalPrice;

            price *= modifier.quantity;
            amount += price;

            this.totalPrice.setAmount(amount);
          }
        });
      }
    );
  }

  /**
   * Calculate discount value
   * @return {void}
   */
  public calculateItemDiscount(): void {
    if (!this.discount) {
      return;
    }

    // case this.discount.type === DISCOUNT_TYPES.FIXED
    let discountValue = this.discount.amount;
    let { amount } = this.totalPrice;

    if (this.discount.type === DISCOUNT_TYPES.PERCENTAGE) {
      discountValue = (amount * this.discount.amount) / 100;
    }

    amount -= discountValue;
    this.totalPrice.setAmount(amount);
  }

  /**
   * Calculate tax value
   * @return {void}
   */
  public calculateItemTax(): void {
    this.taxes.map((tax: TaxInterface) => {
      if (tax.inclusion_type === TAXES_INCLUSION_TYPE.ADDITIVE) {
        let { amount } = this.totalPrice;
        const taxValue = (amount *= tax.rate / 100) || 0;
        amount += taxValue;
        this.totalPrice.setAmount(amount);
      }
    });
  }

  /**
   * Get selected variation
   * @return {VariationInterface} variation
   */
  public getSelectedVariations(): VariationInterface {
    return <VariationInterface>this.variations?.find((variation: VariationInterface) => variation.selected);
  }

  /**
   * Get selected modifiers
   * @return {Array<ModifiersInterface>} modifiers
   */
  public getSelectedModifiers(): Array<ModifiersInterface> {
    const modifiers: Array<ModifiersInterface> = [];
    let index = 0;
    this.modifier_lists?.map((list: ModifierListInterface) =>
      list.modifiers.map((modifier: ModifiersInterface) => {
        if (modifier.selected) {
          modifiers.push(
            new Modifier(<ModifiersType>{
              price: modifier.price_money.amount,
              id: modifier.id,
              name: modifier.name,
              local_name: modifier?.local_name,
              quantity: modifier.quantity || 1,
              cost: modifier.cost,
              sorting: index++,
              order: index++,
            })
          );
        }
      })
    );

    return modifiers;
  }

  /**
   * Increase item quantity
   * @return {void}
   */
  public increase(): void {
    this.quantity++;
  }

  /**
   * Decrease item quantity
   * @return {void}
   */
  public decrease(): void {
    this.quantity--;
  }

  public selectedVariation(): VariationInterface {
    return <VariationInterface>this.variations?.find((i) => i.selected);
  }

  public selectedModifiers(): Array<ModifiersInterface> {
    const modifiersList: Array<ModifiersInterface> = [];
    this.modifier_lists?.forEach((modifierList) => {
      modifierList?.modifiers?.forEach((modifier) => {
        if (modifier.selected) {
          modifiersList.push(modifier);
        }
      });
    });

    return modifiersList;
  }

  isEqual(item: ItemInterface): boolean {
    // // check item id || name
    if (this.name !== item.name) {
      return false;
    }

    if (this.selectedVariation().id !== item.selectedVariation().id) {
      // check variations
      return false;
    }

    if (this.selectedVariation().priceValue() !== item.selectedVariation().priceValue()) {
      // check the variation price
      return false;
    }

    const selectedModifiersIdsA = this.selectedModifiers().flatMap((i) => i.id);
    const selectedModifiersIdsB = item.selectedModifiers().flatMap((i) => i.id);

    // check modifiers
    if (!window._.isEqual(selectedModifiersIdsA, selectedModifiersIdsB)) {
      return false;
    }

    // check notes
    if (window._.trim(this.notes) !== window._.trim(item.notes)) {
      return false;
    }

    // check discount and skip the sale fixed discount
    return !!window._.isEqual(this.discount, item.discount);
  }

  /**
   * Update quantities
   * @return {void}
   */
  public updateQuantities(item: ItemInterface): void {
    this.quantity += item.quantity;
  }

  /***
   * Get Modifiers names
   * @return {string}
   */
  public getModifiersNames(): string {
    const strings: Array<String> = [];
    this.modifier_lists?.forEach((item) => {
      strings.push(item.name);
    });

    return strings.join(",") || "--";
  }

  /**
   * checks if the item is in the staged array
   * @param stagedItems
   * @return {string}
   */
  public checkIsStaged(stagedItems: Array<Item>): boolean {
    return !!stagedItems?.find((item) => item.original_name === this.original_name);
  }

  /**
   * Replace With catalog variations
   * @param catalogVariations
   * @return {Array<VariationInterface>}
   */
  public replaceWithCatalogVariations(catalogVariations) {
    return catalogVariations?.map((obj) => this.variations?.find((item) => item.id === obj.id) || obj);
  }

  /**
   * Replace With catalog modifier list
   * @param catalogVariations
   */
  public replaceWithCatalogModifierLists(catalogVariations) {
    return catalogVariations?.map((obj) => this.modifier_list?.find((item) => item.id === obj.id) || obj);
  }

  /**
   * Find index of item
   * @param originalCatalogItems
   * @return {number}
   */
  public findIndexOfItem(originalCatalogItems): number {
    return originalCatalogItems?.findIndex((item) => item.id === this.id);
  }

  /**
   * replace modifier_lists with modifier_list for ooc use
   */
  public prepareOocItem(this) {
    this.modifier_list = this.modifier_lists;
    delete this.modifier_lists;
    return this;
  }

  /***
   * Get Modifiers name ooc use
   * @return {string}
   */
  public getModifiersNamesOoc(): string {
    const strings: Array<String> = [];
    this.modifier_list.forEach((item) => {
      strings.push(item.name);
    });

    return strings.join(",") || "--";
  }

  public validate(): validateItemInterface {
    const validate: validateOrderInterface = {
      success: true,
    };
    if (!this.getSelectedVariations()?.validate()) {
      validate.success = false;
      validate.message = $t("custom_variation_price_required");
      validate.key = "variation_error";
    }

    if (this?.discount && !this?.discount.validateValue()) {
      validate.success = false;
      validate.key = "discount_error";

      if (this.discount?.type === DISCOUNT_TYPES.PERCENTAGE) {
        validate.message = `${$t("validate_percent_number_value")}`;
      }

      if (this.discount?.type === DISCOUNT_TYPES.FIXED) {
        validate.message = `${$t("validate_number_value")}`;
      }
    }

    return validate;
  }
}
